import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../services/api';
import type { DashboardStats } from '../types';
import { useAuth } from '../contexts/AuthContext';
import {
  Users,
  Building2,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign,
  ArrowUpRight,
  Loader2,
  PieChart as PieChartIcon,
  BarChart3,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
const STATUS_COLORS: Record<string, string> = {
  Nouveau: '#9ca3af',
  'En cours': '#3b82f6',
  Transféré: '#10b981',
  Terminé: '#059669',
  Rejeté: '#ef4444',
  Annulé: '#f97316',
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const userRole = user?.advisor?.role?.name;
  const isAdmin = userRole?.toLowerCase() === 'admin';

  const { data: stats, isLoading, error } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await dashboardApi.getStats();
      return response.data;
    },
    enabled: isAdmin,
    refetchInterval: 60000, // Refresh every minute
  });

  // Non-admin dashboard
  if (!isAdmin) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Bienvenue, {user?.name}</p>
          <p className="text-xs text-gray-400 mt-1">Role: {userRole || 'non défini'}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Actions rapides
          </h2>
          <div className="space-y-3">
            <a
              href="/customers"
              className="block px-4 py-3 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition"
            >
              Gérer les clients
            </a>
            <a
              href="/pension-funds"
              className="block px-4 py-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition"
            >
              Gérer les caisses
            </a>
            <a
              href="/workflow"
              className="block px-4 py-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition"
            >
              Voir le workflow
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Error state
  if (error || !stats) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const axiosError = error as any;
    const responseError = axiosError?.response?.data?.error || '';

    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700 font-medium">Erreur lors du chargement du dashboard</p>
        <p className="text-red-600 text-sm mt-2">{errorMessage}</p>
        {responseError && <p className="text-red-600 text-xs mt-1">{responseError}</p>}
      </div>
    );
  }

  const formatCurrency = (value: number) =>
    `CHF ${value.toLocaleString('fr-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const formatCurrencyShort = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toString();
  };

  // Prepare data for charts
  type ByFondation = DashboardStats['pension_funds']['by_fondation'][string];
  type ByStatus = DashboardStats['pension_funds']['by_status'][string];
  type ByState = DashboardStats['customers']['by_state'][number];
  type MonthlyTrend = DashboardStats['monthly_trends'][number];

  const fondationData = (Object.values(stats.pension_funds.by_fondation) as ByFondation[]).map((f) => ({
    name: f.fondation,
    transferred: f.transferred_amount,
    count: f.count,
  }));

  const statusData = (Object.entries(stats.pension_funds.by_status) as [string, ByStatus][])
    .filter(([_, data]) => data.count > 0)
    .map(([status, data]) => ({
      name: status,
      count: data.count,
      amount: data.total_amount,
      fill: STATUS_COLORS[status] || '#9ca3af',
    }));

  const customerStateData = stats.customers.by_state.map((s: ByState) => ({
    name: s.state_name,
    count: s.count,
  }));

  // Custom tooltip for currency values
  const CurrencyTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ color: string; name: string; value: number }>; label?: string }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-3">
          <p className="font-medium text-gray-900 mb-1">{label}</p>
          {payload.map((entry: { color: string; name: string; value: number }, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.name.includes('amount') || entry.name === 'Montant'
                ? formatCurrency(entry.value)
                : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Admin</h1>
        <p className="text-gray-600 mt-1">Vue d'ensemble des KPIs</p>
      </div>

      {/* Main KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Amount to Transfer */}
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Montant à transférer</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(stats.pension_funds.amount_to_transfer)}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <TrendingUp className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className="text-gray-500">
              {(Object.values(stats.pension_funds.by_status) as ByStatus[])
                .filter((s) => ['Nouveau', 'En cours'].includes(s.status))
                .reduce((acc: number, s) => acc + s.count, 0)}{' '}caisses en attente
            </span>
          </div>
        </div>

        {/* Amount Transferred */}
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Montant transféré</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(stats.pension_funds.amount_transferred)}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className="text-gray-500">
              {(Object.values(stats.pension_funds.by_status) as ByStatus[])
                .filter((s) => ['Transféré', 'Terminé'].includes(s.status))
                .reduce((acc: number, s) => acc + s.count, 0)}{' '}transferts complétés
            </span>
          </div>
        </div>

        {/* Interventions to Handle */}
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Interventions à traiter</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats.interventions.to_handle}
              </p>
            </div>
            <div className={`${stats.interventions.to_handle > 0 ? 'bg-orange-100' : 'bg-gray-100'} p-3 rounded-lg`}>
              <AlertTriangle className={`h-6 w-6 ${stats.interventions.to_handle > 0 ? 'text-orange-600' : 'text-gray-400'}`} />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className="text-gray-500">
              {stats.interventions.pending} en attente, {stats.interventions.in_progress} en cours
            </span>
          </div>
        </div>

        {/* Total Commissions */}
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Commissions totales</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(stats.pension_funds.total_commissions)}
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className="text-gray-500">
              Moyenne: {formatCurrency(stats.pension_funds.average_amount)}/caisse
            </span>
          </div>
        </div>
      </div>

      {/* Second row KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Total Customers */}
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total clients</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats.customers.total_count.toLocaleString('fr-CH')}
              </p>
            </div>
            <div className="bg-indigo-100 p-3 rounded-lg">
              <Users className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
            <span className="text-green-600 font-medium">+{stats.customers.new_this_month}</span>
            <span className="text-gray-500 ml-1">ce mois</span>
          </div>
        </div>

        {/* Total Pension Funds */}
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total caisses</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats.pension_funds.total_count.toLocaleString('fr-CH')}
              </p>
            </div>
            <div className="bg-cyan-100 p-3 rounded-lg">
              <Building2 className="h-6 w-6 text-cyan-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className="text-gray-500">
              {stats.monthly_trends[5]?.new_pension_funds || 0} nouvelles ce mois
            </span>
          </div>
        </div>

        {/* Customers with Intervention */}
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Dossiers en intervention</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats.customers.with_intervention}
              </p>
            </div>
            <div className={`${stats.customers.with_intervention > 0 ? 'bg-amber-100' : 'bg-gray-100'} p-3 rounded-lg`}>
              <Clock className={`h-6 w-6 ${stats.customers.with_intervention > 0 ? 'text-amber-600' : 'text-gray-400'}`} />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className="text-gray-500">
              {stats.interventions.resolved_this_month} résolues ce mois
            </span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* By Fondation - Bar Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <BarChart3 className="h-5 w-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Montants transférés par fondation</h2>
          </div>
          <div className="h-72">
            {fondationData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={fondationData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis
                    type="number"
                    tickFormatter={formatCurrencyShort}
                    fontSize={12}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={80}
                    fontSize={12}
                  />
                  <Tooltip content={<CurrencyTooltip />} />
                  <Bar dataKey="transferred" name="Transféré" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                Aucune donnée disponible
              </div>
            )}
          </div>
        </div>

        {/* By Status - Pie Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <PieChartIcon className="h-5 w-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Répartition par statut</h2>
          </div>
          <div className="h-72">
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="count"
                    label={({ name, payload }) => `${name}: ${payload?.count || 0}`}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, _name, props) => [
                      `${value} caisses (${formatCurrency(props.payload?.amount || 0)})`,
                      props.payload?.name || ''
                    ]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                Aucune donnée disponible
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Customers by State - Bar Chart */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center mb-4">
          <Users className="h-5 w-5 text-gray-400 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Clients par état</h2>
        </div>
        <div className="h-72">
          {customerStateData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={customerStateData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" fontSize={12} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={160}
                  fontSize={11}
                  tick={{ fill: '#4b5563' }}
                />
                <Tooltip />
                <Bar dataKey="count" name="Clients" fill="#6366f1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              Aucune donnée disponible
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats Footer */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 text-center border border-blue-200">
          <p className="text-2xl font-bold text-blue-900">
            {stats.monthly_trends.reduce((acc: number, m: MonthlyTrend) => acc + m.new_customers, 0)}
          </p>
          <p className="text-sm text-blue-700">Nouveaux clients (6 mois)</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center border border-green-200">
          <p className="text-2xl font-bold text-green-900">
            {stats.monthly_trends.reduce((acc: number, m: MonthlyTrend) => acc + m.completed_transfers, 0)}
          </p>
          <p className="text-sm text-green-700">Transferts complétés (6 mois)</p>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 text-center border border-purple-200">
          <p className="text-2xl font-bold text-purple-900">
            {formatCurrency(stats.monthly_trends.reduce((acc: number, m: MonthlyTrend) => acc + m.amount_transferred, 0))}
          </p>
          <p className="text-sm text-purple-700">Total transféré (6 mois)</p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 text-center border border-amber-200">
          <p className="text-2xl font-bold text-amber-900">
            {stats.monthly_trends.reduce((acc: number, m: MonthlyTrend) => acc + m.interventions_resolved, 0)}
          </p>
          <p className="text-sm text-amber-700">Interventions résolues (6 mois)</p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
